// SPDX-License-Identifier: FSL-1.1-Apache-2.0
// packages/solidjs/src/lib/analyticsCopy.ts

/**
 * Geenius Analytics SolidJS - Analytics Copy
 *
 * @module SolidjsAnalyticsCopy
 * @description Provides localized analytics UI copy helpers for the SolidJS
 * variant, falling back to shared package translations when no i18n provider is
 * mounted by the host app.
 */

import {
  formatAnalyticsTime,
  getAnalyticsDeviceLabel,
  pluralizeAnalytics,
  translateAnalytics,
} from "@geenius/analytics";
import { createI18n } from "@geenius/i18n/solidjs";

type AnalyticsParams = Record<string, string | number>;

const LOCAL_ANALYTICS_TRANSLATIONS: Record<string, string> = {
  "analytics.cohorts.columns.cohort": "Cohort",
  "analytics.cohorts.columns.lastActivity": "Last activity",
  "analytics.cohorts.columns.size": "Size",
  "analytics.consent.state": "Consent state",
  "analytics.consent.status.notRecorded": "Not recorded",
  "analytics.consent.status.off": "Off",
  "analytics.consent.status.on": "On",
  "analytics.topPages.columns.averageTime": "Avg. time",
  "analytics.topPages.progressLabel": "{{url}} page views: {{views}}",
};

function buildPluralKey(key: string, count: number): string {
  if (count === 0) {
    return `${key}_zero`;
  }

  if (count === 1) {
    return `${key}_one`;
  }

  return `${key}_other`;
}

function interpolate(
  template: string,
  params: AnalyticsParams | undefined,
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_match, key: string) =>
    String(params?.[key] ?? ""),
  );
}

function translateWithLocalFallback(
  key: string,
  params?: AnalyticsParams,
): string {
  const translated = translateAnalytics(key, params);
  if (translated !== key) {
    return translated;
  }

  const localFallback = LOCAL_ANALYTICS_TRANSLATIONS[key];
  return localFallback ? interpolate(localFallback, params) : translated;
}

function formatNumber(value: number, locale?: string): string {
  return new Intl.NumberFormat(locale).format(value);
}

function formatPercent(value: number, locale?: string): string {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
    style: "percent",
  }).format(value / 100);
}

function formatDuration(milliseconds: number, locale?: string): string {
  const totalSeconds = Math.max(0, Math.round(milliseconds / 1_000));
  const formatter = new Intl.NumberFormat(locale);
  if (totalSeconds < 60) {
    return `${formatter.format(totalSeconds)}s`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds > 0
    ? `${formatter.format(minutes)}m ${formatter.format(seconds)}s`
    : `${formatter.format(minutes)}m`;
}

export function createAnalyticsCopy(): {
  t: (key: string, params?: AnalyticsParams) => string;
  plural: (key: string, count: number, params?: AnalyticsParams) => string;
  deviceLabel: (device: string) => string;
  formatTime: (value: number | string, locale?: string) => string;
  formatNumber: (value: number, locale?: string) => string;
  formatPercent: (value: number, locale?: string) => string;
  formatDuration: (milliseconds: number, locale?: string) => string;
} {
  let providerTranslator:
    | ((key: string, params?: AnalyticsParams) => string)
    | null = null;

  try {
    providerTranslator = createI18n().t;
  } catch {
    providerTranslator = null;
  }

  const translateWithProvider = (
    key: string,
    params?: AnalyticsParams,
  ): string | null => {
    if (!providerTranslator) {
      return null;
    }

    const translated = providerTranslator(key, params);
    return translated !== key ? translated : null;
  };

  const t = (key: string, params?: AnalyticsParams) =>
    translateWithProvider(key, params) ??
    translateWithLocalFallback(key, params);

  const plural = (key: string, count: number, params?: AnalyticsParams) => {
    const translatedPlural = translateWithProvider(buildPluralKey(key, count), {
      count,
      ...params,
    });

    if (translatedPlural) {
      return translatedPlural;
    }

    return pluralizeAnalytics(key, count, params);
  };

  return {
    t,
    plural,
    deviceLabel: (device) => getAnalyticsDeviceLabel(device, t),
    formatTime: (value, locale) => formatAnalyticsTime(value, locale),
    formatNumber,
    formatPercent,
    formatDuration,
  };
}
